import { EIdentify, EPrioritize } from './interfaces';
import type { AttributesLayoutHelper } from '#lib/components/Attributes.types.js';
import type { FeatureRow, TableColumnDef } from '#lib/components/table/tanstackFeatures.js';
import { calculateCrashRisk, predictedCrashes10yr } from './Countermeasures/safeIntersectionsLib';
import { sortByOrder } from '../priority';

// --- Feature property types ---

export type TIdentify = {
	id: number;
	state: string;
	county: string;
	tribe: string;
	major_road_name: string;
	minor_road_name: string;
	intersection_class: string;
	crash_risk_model: string;
	qmajor: number | null;
	qminor: number | null;
	minor_approach_skew_angle: number;
	minor_approach_distance_m: number;
	qminor_source: string;
	qmajor_source: string;
};

export type TPrioritize = {
	id: number;
	state: string;
	county: string;
	tribe: string;
	intersection_priority: string;
	priority_rating_rank: number;
	crash_risk_model: string;
	qminor_source: string;
	qmajor_source: string;
};

// --- Lookup maps ---
// Cross-app FHWA mappings live in `#lib/common/mappings.ts`; only
// SafeIntersections-specific maps are below. Raw priority values differ from
// SafeCurves' ("High"/"Medium High"/… vs "HIGH"/"MEDIUM-HIGH"/…).

export const mapPriorityDescription: Record<string, string> = {
	High: 'Priority 1',
	'Medium High': 'Priority 2',
	Medium: 'Priority 3',
	'Low Medium': 'Priority 4',
	Low: 'Priority 5'
};

const priorityDescriptionOrder = ['High', 'Medium High', 'Medium', 'Low Medium', 'Low'];

/**
 * "Predicted crashes (10yr)" — computed per-row from the crash-risk model
 * inputs carried on the Identify tiles. Empty string when any input is null
 * (unclassifiable intersection).
 */
function predictedCrashesCell(row: TIdentify): string {
	const { qminor, qmajor, minor_approach_skew_angle, minor_approach_distance_m } = row;
	if (
		qminor !== null &&
		qmajor !== null &&
		minor_approach_skew_angle !== null &&
		minor_approach_distance_m !== null
	) {
		const risk = calculateCrashRisk(
			qminor,
			qmajor,
			minor_approach_skew_angle,
			minor_approach_distance_m
		);
		const rate = predictedCrashes10yr(risk);
		if (rate) return rate.toFixed(1);
	}
	return '';
}

// --- Attributes panel layout definitions ---

const attributesIdentify: AttributesLayoutHelper = {
	title: { accessorKey: 'intersection_class' },
	subheaders: [[{ accessorKey: 'county' }]],
	table: [
		{ accessorKey: 'crash_risk_model', header: () => 'Intersection type' },
		{ accessorKey: 'major_road_name', header: () => 'Priority leg road name(s)' },
		{ accessorKey: 'minor_road_name', header: () => 'Controlled leg road name(s)' }
	]
};

const attributesPrioritize: AttributesLayoutHelper = {
	title: {
		accessorKey: 'intersection_priority',
		cell: (value) => mapPriorityDescription[value as string]
	},
	subheaders: [[{ accessorKey: 'county' }]],
	body: [
		{
			accessorKey: 'priority_rating_rank',
			header: () => 'Priority rating rank',
			show: (info) => info !== undefined
		}
	],
	table: [{ accessorKey: 'crash_risk_model', header: () => 'Intersection type' }]
};

export const attributesColumns: Record<string, AttributesLayoutHelper> = {
	[EIdentify.Identify]: attributesIdentify,
	[EPrioritize.Prioritize]: attributesPrioritize
};

// --- Table column definitions (per Table tab) ---

type TableTabId = 'Identify' | 'Prioritize';

export const tableColumnsByTab: Record<TableTabId, TableColumnDef<FeatureRow>[]> = {
	Identify: [
		{ accessorKey: 'county', header: 'Location' },
		{ accessorKey: 'intersection_class', header: 'Intersection class' },
		{ accessorKey: 'crash_risk_model', header: 'Intersection type' },
		{
			accessorKey: 'id',
			header: 'Predicted crashes (10yr)',
			cell: (info) => predictedCrashesCell(info.row.original as unknown as TIdentify)
		}
	],
	Prioritize: [
		{ accessorKey: 'county', header: 'Location' },
		{
			accessorKey: 'intersection_priority',
			header: 'Intersection priority',
			cell: (info) =>
				mapPriorityDescription[info.getValue() as string] ?? String(info.getValue() ?? ''),
			sortFn: sortByOrder(priorityDescriptionOrder)
		},
		{ accessorKey: 'priority_rating_rank', header: 'Priority Rating Rank' },
		{ accessorKey: 'crash_risk_model', header: 'Intersection type' }
	]
};
